/**
 * Pen input dynamics — pressure & tilt → drawing parameters.
 *
 * Pure, DOM-free so it can be unit-tested directly (the pointer-event plumbing
 * that reads `PointerEvent.pressure`/`tiltX`/`tiltY` lives in TileCanvas.vue,
 * the stamping that consumes these numbers lives in useDrawing.ts — neither is
 * unit-testable without a real canvas, so the decision maths is isolated here).
 *
 * Convention for "is there a real pressure sensor?": Safari reports a sentinel
 * `pressure === 0.5` for devices with no pressure sensor (finger, mouse,
 * non-Pencil stylus). TileCanvas already uses `pressure > 0 && pressure !== 0.5`
 * to detect a genuine reading — we carry that decision in `hasPressure`.
 *
 * Observed on-device: a value that is NEITHER 0 NOR the 0.5 sentinel, but
 * never varies (reported as a fixed 0.120 regardless of applied force) —
 * likely a hardware/pairing issue, not something fixable here, but the
 * sentinel check alone can't tell "genuine, currently steady" apart from
 * "not actually a working sensor." PressureReliabilityTracker below watches
 * a rolling window of samples and stops treating them as genuine once
 * they've shown no variation at all over enough of them — self-correcting
 * if a future pencil/pairing/firmware update starts reporting real values.
 */

/** One normalised pen sample. `tiltMag` is `hypot(tiltX, tiltY)` in degrees. */
export interface PenInput {
	/** raw PointerEvent.pressure, 0–1 (0.5 = sentinel / no sensor) */
	pressure: number
	/** true when `pressure` is a genuine reading (not the 0.5 sentinel / 0) */
	hasPressure: boolean
	/** tilt magnitude in degrees: 0 = upright, ~90 = laid flat. 0 for finger/mouse. */
	tiltMag: number
	/** originating device (`PointerEvent.pointerType`) — 'pen' reads as a
	 *  stylus for the weight baseline below, anything else (or absent, for
	 *  tests/programmatic strokes) reads as a finger. */
	pointerType?: 'mouse' | 'touch' | 'pen'
}

/** Default sample for callers with no pointer data (tests, programmatic strokes). */
export const NO_PEN_INPUT: PenInput = {
	pressure: 0.5,
	hasPressure: false,
	tiltMag: 0,
}

function clamp01(x: number): number {
	return x < 0 ? 0 : x > 1 ? 1 : x
}

/** Samples needed before judging reliability at all — assume genuine until then. */
const RELIABILITY_MIN_SAMPLES = 15
/** Rolling window size — old samples age out, so a sensor that starts working mid-session recovers. */
const RELIABILITY_WINDOW = 30
/** Minimum (max − min) over the window to call it "varying" rather than stuck. */
const RELIABILITY_EPSILON = 0.01

export interface PressureReliabilityTracker {
	/** Feed one raw pressure reading. No-op for 0 or the 0.5 sentinel — those
	 *  already mean "no sensor" on their own, nothing to learn from them. */
	observe(pressure: number): void
	/** True until proven stuck: needs RELIABILITY_MIN_SAMPLES samples showing
	 *  no variation beyond RELIABILITY_EPSILON before returning false. */
	isReliable(): boolean
}

/** Fresh tracker with its own rolling window — one per drawing session (e.g.
 *  one instance for the lifetime of TileCanvas.vue), not shared across tests. */
export function createPressureReliabilityTracker(): PressureReliabilityTracker {
	const samples: number[] = []
	return {
		observe(pressure: number): void {
			if (pressure <= 0 || pressure === 0.5) return
			samples.push(pressure)
			if (samples.length > RELIABILITY_WINDOW) samples.shift()
		},
		isReliable(): boolean {
			if (samples.length < RELIABILITY_MIN_SAMPLES) return true
			return Math.max(...samples) - Math.min(...samples) > RELIABILITY_EPSILON
		},
	}
}

// ---------------------------------------------------------------------------
// Pencil (sketch) — pressure → stamp weight multiplier
// ---------------------------------------------------------------------------
//
// The stamp weight written per touched pixel is `STAMP_WEIGHT × weightMul`
// (useDrawing.ts). `STAMP_WEIGHT` (0.1) is the historical "full" weight — the
// old flat default before Apple Pencil support.
//
// Brief 09-layout: the pencil should read distinctly THINNER with a stylus and
// THICKER with a finger — device-aware, not one shared curve — while a stylus
// pressed at an ordinary, unremarkable force should still look "similar to"
// finger drawing (so switching device mid-sketch isn't jarring), and the
// stylus's overall dynamic range should be wider than before.
//
//   • Finger/mouse (never reports real pressure): a flat, deliberately
//     THICKER baseline than the old shared default.
//   • Stylus, genuine pressure: light press reads clearly thinner than the
//     finger baseline, and the midpoint (~0.5) lands well below it — on
//     device even the first pulled-down curve (mid-press ~0.4) still read
//     as too thick/dark with an Apple Pencil, so it's halved again.
//         p→0   → 0.035  (feather-light — thinner than any finger stroke)
//         p=0.5 → 0.1975 (an ordinary press — well below the finger baseline)
//         p→1   → 0.36   (firm press — still short of the finger baseline)
//   • Stylus, no usable pressure reading (a real device-quirk on some
//     hardware — see the module doc above): a flat fallback that still reads
//     thinner than the finger baseline, not a borrowed finger default.

/** Flat weight multiplier for a finger/mouse stroke (no pressure sensor at
 *  all) — raised from the old universal 0.4 so plain-finger sketching reads
 *  as the thicker of the two defaults. */
export const PENCIL_FINGER_WEIGHT_MUL = 0.55
/** Stylus weight multiplier at pressure 0 — halved again (0.07 → 0.035)
 *  alongside the range below; on-device an Apple Pencil still read too
 *  thick/dark against the finger baseline even after the first reduction. */
export const PENCIL_STYLUS_PRESSURE_MIN_MUL = 0.035
/** Added stylus weight multiplier across the full 0→1 pressure range —
 *  halved again (0.65 → 0.325): on-device firm Pencil presses were still
 *  reading too thick/dark after the first reduction. */
export const PENCIL_STYLUS_PRESSURE_RANGE_MUL = 0.325
/** Flat fallback for a stylus whose pressure sensor is absent/unreliable —
 *  still reads as stylus-thin, not the finger baseline (some hardware never
 *  reports usable Pencil pressure, so this is what most of its strokes get).
 *  Halved again (0.26 → 0.13) in step with the pressure curve above. */
export const PENCIL_STYLUS_FALLBACK_MUL = 0.13

export function pencilWeightMul(input: PenInput): number {
	const isStylus = input.pointerType === 'pen'
	if (!input.hasPressure) return isStylus ? PENCIL_STYLUS_FALLBACK_MUL : PENCIL_FINGER_WEIGHT_MUL
	// Genuine pressure implies a stylus — finger/mouse never report it.
	return PENCIL_STYLUS_PRESSURE_MIN_MUL + PENCIL_STYLUS_PRESSURE_RANGE_MUL * clamp01(input.pressure)
}

// ---------------------------------------------------------------------------
// Pencil (sketch) — tilt → stamp radius bucket
// ---------------------------------------------------------------------------
//
// Laying the Pencil over spreads the mark, mimicking shading with the side of a
// graphite tip. Kept modest — this stays a pencil, not an airbrush. Tilt is
// quantised into a small fixed set of buckets so useDrawing can cache one
// sprite set per (theme, bucket) instead of regenerating sprites per stamp.

/** Tilt (degrees) at which the spread saturates — beyond this, no extra width. */
export const TILT_SATURATION_DEG = 60
/**
 * Number of discrete tilt/radius buckets (bucket 0 = upright). Bumped from 4
 * to 6 alongside widening the max radius gain — a bigger range with too few
 * steps would make the jump between buckets itself visible/steppy.
 */
export const N_TILT_BUCKETS = 6

/** Continuous 0→1 spread factor for a tilt magnitude (before quantisation). */
export function tiltSpread(tiltMag: number): number {
	return clamp01(tiltMag / TILT_SATURATION_DEG)
}

/** Quantise a tilt magnitude to a bucket index in [0, N_TILT_BUCKETS-1]. */
export function tiltBucket(tiltMag: number): number {
	return Math.round(tiltSpread(tiltMag) * (N_TILT_BUCKETS - 1))
}

// ---------------------------------------------------------------------------
// Ink — pressure → line-width multiplier
// ---------------------------------------------------------------------------
//
// The S/M/L selector stays the *centre* size: mid pressure reproduces it, light
// press thins the line, firm press thickens it — live within one stroke.
//
//       widthMul(p) = MIN + RANGE·p
//         p→0   → 0.5   (half the selected width)
//         p=0.5 → 1.0   (exactly the selected width)
//         p→1   → 1.5   (1.5× the selected width)

/** Line-width multiplier at pressure 0. */
export const INK_PRESSURE_MIN_MUL = 0.5
/** Added line-width multiplier across the full 0→1 pressure range. */
export const INK_PRESSURE_RANGE_MUL = 1.0

export function inkWidthMul(input: PenInput): number {
	if (!input.hasPressure) return 1
	return INK_PRESSURE_MIN_MUL + INK_PRESSURE_RANGE_MUL * clamp01(input.pressure)
}
